import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, StarIcon, PlayCircleIcon, CheckIcon, ServerIcon, InfinityIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePricing } from '../contexts/PricingContext';
import { ContactFormPopup } from './ContactFormPopup';
export function HeroVariant11() {
  const {
    t
  } = useLanguage();
  const {
    price
  } = usePricing();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const wordItem = {
    hidden: {
      opacity: 0,
      y: 20,
      filter: 'blur(8px)'
    },
    show: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <>
      <section className="relative min-h-screen w-full overflow-hidden pt-32 pb-20 flex items-center" style={{
      backgroundColor: 'var(--bg-primary)'
    }} data-id="element-317">
        <div className="absolute top-1/4 left-0 w-[800px] h-[600px] opacity-30 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at center, var(--accent) 0%, transparent 70%)'
      }} data-id="element-318" />

        <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col lg:flex-row items-center justify-between gap-12 px-4" data-id="element-319">
          {/* Left Column - Text & CTAs */}
          <div className="w-full lg:w-1/2 text-left flex flex-col items-start" data-id="element-320">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-4 md:mb-6 bg-white/50 backdrop-blur-sm shadow-sm" style={{
            borderColor: 'var(--border)'
          }} data-id="element-321">
              <span className="text-xs md:text-sm font-semibold tracking-wide text-gray-800 flex items-center gap-1.5" data-id="element-322">
                🔥 {t('hero.badge')}
              </span>
            </motion.div>

            <motion.h1 variants={container} initial="hidden" animate="show" className="mb-6 md:mb-8 text-4xl leading-tight tracking-tight md:text-5xl lg:text-[3.5rem] font-bold max-w-2xl" style={{
            color: 'var(--text-primary)'
          }} data-id="element-323">
              <motion.span variants={wordItem} className="block mb-1" data-id="element-324">
                {t('hero.title').replace('бесплатно.', '').replace('безкоштовно.', '').replace('for free.', '')}
              </motion.span>
              <motion.span variants={wordItem} className="relative inline-block mb-2" data-id="element-325">
                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 uppercase tracking-wider font-black" data-id="element-326">
                  {t('hero.title').includes('бесплатно') ? 'БЕСПЛАТНО.' : t('hero.title').includes('безкоштовно') ? 'БЕЗКОШТОВНО.' : 'FOR FREE.'}
                </span>
                <motion.div initial={{
                scaleX: 0
              }} animate={{
                scaleX: 1
              }} transition={{
                delay: 1,
                duration: 0.8,
                ease: 'easeOut'
              }} className="absolute bottom-2 left-0 w-full h-4 bg-blue-200/50 -rotate-2 skew-x-12 origin-left" data-id="element-327" />
              </motion.span>
              <br data-id="element-328" />
              <motion.span variants={wordItem} className="inline-block" data-id="element-329">
                {t('hero.titleHighlight').replace('$15/мес.', '').replace('$15/міс.', '').replace('$15/mo.', '')}{' '}
                <span style={{
                color: 'var(--accent)'
              }} data-id="element-330">
                  {price}/мес.
                </span>
              </motion.span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="text-base sm:text-lg md:text-xl mb-6 max-w-xl text-gray-600 font-medium" data-id="element-331">
              {t('hero.subtitle')}
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.8
          }} className="flex flex-col sm:flex-row gap-4 items-center justify-start mb-12 w-full sm:w-auto" data-id="element-332">
              <button onClick={() => setIsPopupOpen(true)} className="group relative w-full sm:w-auto flex items-center justify-center gap-2 rounded-2xl px-8 py-4 font-bold text-lg transition-all hover:scale-105 overflow-hidden" style={{
              backgroundColor: 'var(--accent)',
              color: '#FFFFFF'
            }} data-id="element-333">
                <div className="absolute inset-0 w-full h-full bg-white/20 group-hover:translate-x-full -translate-x-full transition-transform duration-500 skew-x-12" data-id="element-334" />
                {t('hero.cta')}
                <ArrowRightIcon className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-335" />
              </button>
            </motion.div>
          </div>

          {/* Right Column - Comparison Cards */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="w-full lg:w-1/2 relative mt-12 lg:mt-0 flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-336">
            {/* Traditional Agency Card */}
            <div className="w-full sm:w-1/2 bg-gray-50 rounded-3xl p-6 border border-gray-200 opacity-70 scale-95 shadow-sm" data-id="element-337">
              <div className="text-sm font-bold text-gray-400 mb-4 uppercase tracking-wide" data-id="element-338">
                Обычная студия
              </div>
              <div className="space-y-4 mb-6" data-id="element-339">
                <div className="flex items-start gap-2 text-gray-500" data-id="element-340">
                  <XIcon className="w-5 h-5 text-red-400 shrink-0 mt-0.5" data-id="element-341" />
                  <span className="text-sm" data-id="element-342">Оплата до результата</span>
                </div>
                <div className="flex items-start gap-2 text-gray-500" data-id="element-343">
                  <XIcon className="w-5 h-5 text-red-400 shrink-0 mt-0.5" data-id="element-344" />
                  <span className="text-sm" data-id="element-345">Платные правки</span>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200" data-id="element-346">
                <div className="text-xs text-gray-400 mb-1" data-id="element-347">Разработка</div>
                <div className="text-2xl font-bold text-gray-600" data-id="element-348">$1000+</div>
                <div className="text-xs text-gray-400 mt-2 mb-1" data-id="element-349">Поддержка</div>
                <div className="text-lg font-semibold text-gray-500" data-id="element-350">
                  $50/мес
                </div>
              </div>
            </div>

            {/* Jetly Card */}
            <div className="w-full sm:w-1/2 bg-white rounded-3xl p-6 border-2 shadow-2xl relative z-10 scale-105" style={{
            borderColor: 'var(--accent)'
          }} data-id="element-351">
              <div className="absolute -top-3 -right-3 bg-green-500 text-white text-xs font-black px-3 py-1 rounded-full shadow-lg transform rotate-12" data-id="element-352">
                ВЫБОР КЛИЕНТОВ
              </div>
              <div className="text-sm font-black mb-4 uppercase tracking-wide" style={{
              color: 'var(--accent)'
            }} data-id="element-353">
                С нами (Jetly)
              </div>
              <div className="space-y-4 mb-6" data-id="element-354">
                <div className="flex items-start gap-2 text-gray-800 font-medium" data-id="element-355">
                  <CheckIcon className="w-5 h-5 text-green-500 shrink-0 mt-0.5" data-id="element-356" />
                  <span className="text-sm" data-id="element-357">Бесплатный макет</span>
                </div>
                <div className="flex items-start gap-2 text-gray-800 font-medium" data-id="element-358">
                  <CheckIcon className="w-5 h-5 text-green-500 shrink-0 mt-0.5" data-id="element-359" />
                  <span className="text-sm" data-id="element-360">Безлимитные правки</span>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-100" data-id="element-361">
                <div className="text-xs text-gray-500 mb-1" data-id="element-362">Разработка</div>
                <div className="text-3xl font-black text-green-500" data-id="element-363">$0</div>
                <div className="text-xs text-gray-500 mt-2 mb-1" data-id="element-364">
                  Поддержка + Хостинг
                </div>
                <div className="text-xl font-bold text-gray-900" data-id="element-365">
                  {price}/мес
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-366" />
    </>;
}